// Start-up policy: the initial-sync retry backoff and the singleton boot helpers.
//
// Extracted from DatabaseWrapper so it can be unit-tested without pulling in
// pouchdb-browser, which references `self` and fails to load under node. It was
// split out of database-wrapper-passthrough.ts, which had become the shared home
// for every testable sync helper regardless of subject (deferred finding
// df-08d4a072f17b).

import { PouchDoc } from '@cf-shared/data-model/pouch';
import { DateTime } from 'luxon';
import { realTimerEnvironment, scheduleRunningTimeTimeout, TimerEnvironment, timeoutNotingFreezeAsync } from './running-clock';
import { DatabaseUnavailableError, DatabaseUnavailableReason } from './database-unavailable-error';

// #3698 — capped-backoff retry policy for the initial sync.
//
// The initial replication runs as a single attempt with no built-in retry
// (live: false, retry: false). The app-level retry loop in
// assureInitialSyncAsync used to stop after a fixed two-entry delay list
// ([3000, 8000]) and then never retry again while the user stayed online on
// the boot splash — an intermittent network stall turned into an indefinite
// "Downloading existing data..." screen. The loop now keeps retrying with
// the capped backoff below while online, bounded by a generous attempt cap.
// PouchDB checkpoints replication progress, so each restart resumes from the
// last sequence number — retries do not redo completed work.

/** First retry delays; later attempts use the cap. */
const INITIAL_SYNC_RETRY_DELAYS_MS = [3000, 8000, 15000];

/** Maximum delay between initial-sync retry attempts. */
export const INITIAL_SYNC_RETRY_DELAY_CAP_MS = 30_000;

/**
 * Attempt cap so the loop cannot run forever (it also stops on going
 * offline, on shutdown, and the moment the sync deferred resolves). With
 * the 60s per-attempt wait budget this covers well over 10 minutes — the
 * reported account recovered ~13 minutes after the stall began.
 */
export const INITIAL_SYNC_MAX_RETRY_ATTEMPTS = 20;

/**
 * Attempt index at which the "Initial sync exhausted retries (online)"
 * Sentry signal is reported. This matches the legacy two-retry budget so
 * prod telemetry for this state fires early, while the loop keeps retrying
 * (report-then-keep-trying).
 */
export const INITIAL_SYNC_EARLY_REPORT_ATTEMPT = 2;

/** Returns the delay before the given retry attempt: 3s, 8s, 15s, then a flat 30s cap. */
export function nextInitialSyncRetryDelayMs(attempt: number): number {
  return INITIAL_SYNC_RETRY_DELAYS_MS[attempt] ?? INITIAL_SYNC_RETRY_DELAY_CAP_MS;
}

/** Result of the singleton-docs existence check. */
export interface SingletonExistsResult {
  /** ids of the requested singleton docs that exist on the remote. */
  ids: string[];
  /** set when the check was abandoned (timed out or errored) and `ids` fell
   *  back to []; null on success. The caller surfaces this to telemetry so the
   *  #3898 boot-wedge guard engaging stays observable — this module is
   *  deliberately node-loadable and has no LoggingService, so it reports the
   *  reason out rather than logging itself. */
  abandonedError: unknown;
}

/**
 * Resolve which of the requested singleton docs already exist on the remote,
 * bounded by a timeout. On timeout OR any error, returns empty ids plus the
 * error in `abandonedError` — the caller then skips the singleton pre-sync
 * optimization and falls through to the main initial sync (whose selector
 * already covers singleton docs). This prevents a hung request from wedging
 * boot before the live sync handler is assigned, which would leave the user DB
 * at dbState:"syncing" + syncHandlerActive:false and block every recovery path
 * while syncMutex stays held (#3898).
 *
 * The limit stays on wall time even when the phone froze the app mid-request (#6096): a
 * connection that was open across a long freeze is exactly the half-open case this guards,
 * and giving up on waking starts a fresh request at once. The timeout error says whether a
 * freeze caused it (`timeoutCausedByFreeze`), so the caller can report it as such.
 */
export async function resolveExistingSingletonIds(
  allDocs: () => Promise<{ rows: Array<{ id: string; error?: unknown }> }>,
  timeoutMs: number,
  env: TimerEnvironment = realTimerEnvironment(),
): Promise<SingletonExistsResult> {
  try {
    const result = await timeoutNotingFreezeAsync(timeoutMs, allDocs(), env);
    return { ids: result.rows.filter(row => !row.error).map(row => row.id), abandonedError: null };
  } catch (err) {
    return { ids: [], abandonedError: err };
  }
}

// L5 (boot-health #3986 / #442) — decide what startLocalDatabaseAsync should do after it
// has opened the local DB.
//
// The bug: whenQueriesReady is resolved only inside commitQueriesAsync, which runs only from
// initializeRemoteDb — and initializeRemoteDb runs only when we are online with couch auth. A
// fully-offline cold boot (or a skipRemote / no-auth start) therefore never resolves
// whenQueriesReady, so every view-backed read (runQueryAsync) hangs forever (#442 "can't start
// in offline mode"). The fix: when we are NOT going to initialize the remote DB, still commit
// the view design docs to the LOCAL db (a local-only write that works offline) so the views
// exist AND whenQueriesReady resolves. A later successful initializeRemoteDb re-commits
// idempotently.
export type StartLocalDbFollowup = 'init-remote' | 'commit-queries-locally' | 'nothing';

export function decideStartLocalDbFollowup(input: {
  /** the caller asked to skip remote init (local-only start). */
  skipRemote: boolean;
  /** is there a couch auth session to initialize the remote DB with? */
  hasCouchAuth: boolean;
  /** is the connection state Online right now? */
  online: boolean;
  /** was a local DB actually opened (false for remoteOnly wrappers)? */
  hasLocalDb: boolean;
}): StartLocalDbFollowup {
  // online + authed + not skipping: the normal path — initializeRemoteDb runs commitQueriesAsync.
  if (!input.skipRemote && input.hasCouchAuth && input.online) return 'init-remote';
  // otherwise (offline, no auth, or skipRemote): if we have a local DB, write the views to it
  // locally so reads don't hang. remoteOnly wrappers have no local DB and no views — nothing to do.
  if (input.hasLocalDb) return 'commit-queries-locally';
  return 'nothing';
}

// #4506: the databases a sweep may still act on — everything the provider has created,
// minus any wrapper whose shutdown has started. Which sweeps take this filtered view and
// which need the complete list is decided in design-docs/pass-through-db.md, "Which
// databases a sweep may act on".
export function selectActiveDatabases<T extends { isShuttingDown: boolean }>(databases: T[]): T[] {
  return databases.filter(database => !database.isShuttingDown);
}

/** May a wrapper that is shutting down close the PouchDB handle on its local store?
 *
 * Only when no OTHER live wrapper is open on that same store. This is a correctness
 * rule and not tidiness — PouchDB hands both wrappers the SAME underlying connection,
 * so the wrong answer here breaks the live one. Nothing is leaked by a skip: the
 * wrapper left holding the store closes it on its own shutdown, when this then answers
 * true. The full reasoning is in design-docs/pass-through-db.md, "A shutdown does not
 * always close the local store, and it must not".
 *
 * A wrapper with no local store (`remoteOnly`) has nothing to decide and answers true,
 * so the caller's own null check stays the only thing that matters for it.
 */
export function mayCloseLocalStore<T extends { isShuttingDown: boolean; localDatabaseName: string | null }>(
  self: T, allDatabases: readonly T[],
): boolean {
  const name = self.localDatabaseName;
  if (!name) return true;
  return !allDatabases.some(other =>
    other !== self && !other.isShuttingDown && other.localDatabaseName === name);
}

// L6/S2 (boot-health #3986) — parse the rows of a batched allDocs({keys, include_docs:true})
// read into a docId→doc map of the docs that actually exist. Missing keys come back as rows
// with an `error` (and no `doc`); deleted docs come back as tombstones. Both are skipped, so a
// caller can treat "absent from the map" as "use the default", exactly like a per-doc not-found.
export function collectFoundDocs(
  rows: Array<{ id?: string; key?: string; error?: unknown; doc?: (PouchDoc | null); value?: { deleted?: boolean } }>,
): Map<string, PouchDoc> {
  const found = new Map<string, PouchDoc>();
  for (const row of rows) {
    if (!row.doc) continue;            // missing key: row has an `error`, no `doc`
    if (row.doc._deleted) continue;    // deleted doc body
    if (row.value?.deleted) continue;  // deleted tombstone row
    if (row.doc._id) found.set(row.doc._id, row.doc);
  }
  return found;
}

// L6/S2 (#3986) — build the ordered plan for initializing the boot singletons. When the
// batched read succeeded (`fetched` is a map), each id is processed with its fetched doc (or
// null if the batch didn't find it). When the batch failed/timed out (`fetched` is null), every
// id falls back to the per-singleton read path — NEVER defaults-for-all. Order is preserved so
// order-dependent updaters still run in sequence (e.g. fitnessState before fitnessPlan).
export interface SingletonInitStep {
  id: string;
  fetchedDoc: PouchDoc | null;
  useFallback: boolean;
}

export function buildSingletonInitPlan(
  orderedIds: string[],
  fetched: Map<string, PouchDoc> | null,
): SingletonInitStep[] {
  return orderedIds.map(id =>
    fetched == null
      ? { id, fetchedDoc: null, useFallback: true }
      : { id, fetchedDoc: fetched.get(id) ?? null, useFallback: false },
  );
}

// L12 (boot-health #3986, #3898 leftover) — max-hold decision for syncMutex. startSyncInternal
// holds syncMutex across awaits; the two known remote calls are already bounded, but there is no
// guard against a NEWLY-introduced un-timed await re-creating the #3898 wedge (mutex held forever
// → every later startSync blocks → sync dead). When the hold exceeds the threshold, report it (so
// the new await is caught) and restart sync once the current hold releases. The timer lives here
// so it is testable with a hand-driven clock; database-wrapper owns the side effects.
export const SYNC_MUTEX_MAX_HOLD_MS = 120_000;

/** What the L12 watchdog knows when it fires. */
export interface SyncMutexHoldFacts {
  /** Wall-clock time since the lock was taken, frozen time included. */
  heldMs: number;
  /** The part of `heldMs` the page was frozen, so it was not running. */
  notRunningMs: number;
}

/** What `armSyncMutexHoldWatchdog` returns; cancel it when the hold ends. */
export interface SyncMutexHoldWatchdog {
  cancel(): void;
}

// #6234: the limit is RUNNING time. When a phone froze the app mid-hold, a wall-time timer fired
// in the first moment after waking and reported the frozen minutes as hold time, although the
// bounded reads inside the start end such a hold on their own milliseconds later. This watchdog
// cancels nothing — it only reports and queues a restart — so firing on waking bought nothing.
// A real hang is still caught once it has held the lock for the full limit of running time.
// design-docs/boot-sequence.md, "#6096", the L12 paragraph.
//
// arm the L12 watchdog for one hold: call onTooLong once if the hold lasts the limit.
// heldMs reads the WALL clock, not env.nowMs(): that is performance.now(), which some platforms
// pause while the device sleeps, and held_ms is meant to include the frozen time.
export function armSyncMutexHoldWatchdog(
  onTooLong: (facts: SyncMutexHoldFacts) => void,
  env: TimerEnvironment = realTimerEnvironment(),
  wallClockMs: () => number = () => DateTime.utc().toMillis(),
): SyncMutexHoldWatchdog {
  const heldSinceMs = wallClockMs();
  const handle = scheduleRunningTimeTimeout(() => {
    onTooLong({ heldMs: wallClockMs() - heldSinceMs, notRunningMs: handle.notRunningMs() });
  }, SYNC_MUTEX_MAX_HOLD_MS, env);
  return { cancel: () => handle.cancel() };
}

/** The in-memory copy of one singleton document, as `DatabaseWrapper` keeps it.
 *  Structural on purpose — the wrapper's own `Singleton` class is private to it,
 *  and this helper only ever needs the one field. */
export interface SingletonSlotLike {
  doc?: any;
}

/**
 * Read a singleton document that start-up has already loaded.
 *
 * **Why this refuses rather than answering `undefined` (#4801).** The wrapper
 * creates an EMPTY slot for every singleton before start-up fills it in, so
 * between those two moments a read answered `undefined` — and the caller could
 * not tell that apart from a document that is genuinely empty. Two things then
 * went wrong quietly. `deepOverwriteCouchSafe` treats a missing destination as
 * nothing to do, so copying a coach's whole program into the training plan
 * became a no-op that reported nothing; and the reads that DID blow up did so
 * as a bare `TypeError` deep inside unrelated code, which says nothing about
 * what actually went wrong.
 *
 * This is the same distinction `DatabaseUnavailableError` was introduced for —
 * "I could not look" is not "there is nothing there" — and it is why that type
 * is reused here rather than a new one invented: callers already handle it, and
 * an unhandled one is already recognised by both of `app-sentry.ts`'s nets.
 *
 * Split out from the wrapper so the rule can be unit tested under node, which
 * cannot load `database-wrapper.ts` at all (it pulls in `pouchdb-browser`).
 */
export function readLoadedSingletonDoc(
  slot: SingletonSlotLike | undefined,
  docId: string,
  databaseIdentifier: string,
): any {
  // `== null` on purpose, so a slot that was never filled in is refused whether the
  // field is missing or explicitly null. Every other value — an EMPTY OBJECT very
  // much included — is a real answer: the slot was loaded, the document simply has
  // nothing in it yet, which is the ordinary state of a brand-new account.
  if (slot?.doc == null) {
    throw new DatabaseUnavailableError(
      databaseIdentifier,
      DatabaseUnavailableReason.SingletonNotLoaded,
      `singleton "${docId}"`,
    );
  }
  return slot.doc;
}
